import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { useState } from 'react'
import {
  Button as RaButton,
  Loading,
  SaveButton,
  SelectInput,
  SimpleForm,
  Toolbar,
  useGetOne,
  useNotify,
  usePermissions,
  useRedirect,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Text } from 'shared/components/Text/Text'

import { changeTeamsBundleSubscription } from 'api/core'
import { SupportedTargetBundleChoices } from 'helpers/constants'
import { getProductNameFromRecord } from 'helpers/utils'
import { hasPermission, Permissions } from '../../Permissions'
import { TeamUpsell } from './TeamUpsell'

const TeamProductChange = (props: any) => {
  const { data: team } = useGetOne('teams', props.match.params.id)

  const { permissions } = usePermissions()
  const canAddOrder = hasPermission(permissions || [], Permissions.CanAddOrder)
  const canAddOrderWithOverrides = hasPermission(permissions || [], Permissions.CanAddOrderWithOverrides)

  const [loading, setTeamLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [targetBundle, setTargetBundle] = useState(null)
  const BundleChangeSaveButton = (props: any) => {
    const redirectTo = useRedirect()
    const notify = useNotify()

    const handleSave = async () => {
      setOpen(false)
      if (targetBundle) {
        const payload = { target_bundle: targetBundle }
        setTeamLoading(true)
        changeTeamsBundleSubscription(team?.id, payload)
          .then((newRecord) => {
            notify(`Successfully changed record!`, { type: 'success' })
            setTeamLoading(false)
            redirectTo(`/teams/${newRecord.id}/show`)
          })
          .catch((error) => {
            setTeamLoading(false)
            notify(JSON.stringify(error.response.data), { type: 'error' })
          })
          .finally(() => {
            setTeamLoading(false)
            setTargetBundle(null)
          })
      }
    }

    const handleClick = (values: any) => {
      setTargetBundle(values.target_bundle_choice)
      setOpen(true)
    }

    return (
      <>
        <FlexBox gap="20px">
          <SaveButton {...props} disabled={loading} onSave={handleClick} />
          <RaButton label="Cancel" disabled={loading} onClick={() => redirectTo(`/teams/${team?.id}/show`)} />
        </FlexBox>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>Confirm Plan Selection</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ color: 'black' }}>
              You are about to change <b>{team?.name}'s</b> plan from <b>{props.current_bundle_name}</b> to{' '}
              <b>{targetBundle}</b>. Do you wish to proceed?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Proceed
            </Button>
            <Button color="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  const BundleChangeToolbar = (props: any) => (
    <Toolbar {...props}>
      <BundleChangeSaveButton {...props} />
    </Toolbar>
  )

  const validateBundleChange = (values: any) => {
    const errors: any = {}
    if (values.target_bundle_choice) {
      const target_bundle_id = SupportedTargetBundleChoices.filter(
        (choice) => choice.id === values.target_bundle_choice
      )[0].name
      if (!values.target_bundle_choice) {
        errors.target_bundle_choice = 'Bundle choice required'
      }
      if (team && target_bundle_id === getProductNameFromRecord(team)) {
        errors.target_bundle_choice = 'Bundle choice cannot be the same as the current bundle'
      }
      return errors
    }
  }

  if (!team) return <Loading />

  const current_bundle_name = getProductNameFromRecord(team)
  const updated_team = { ...team, current_bundle_name }

  return (
    <>
      <Text variant="header2" mt={3}>
        Change plan for {team.name}
      </Text>
      <Text variant="paragraph1" styles="max-width: 600px;" mt={2}>
        The fleet will be asked to complete onboarding tasks for the selected plan.
      </Text>
      <SimpleForm toolbar={<BundleChangeToolbar {...updated_team} />} validate={validateBundleChange}>
        <h3>Current Plan: {current_bundle_name}</h3>
        <SelectInput
          source="target_bundle_choice"
          choices={SupportedTargetBundleChoices}
          label="Select New Plan"
        />
      </SimpleForm>

      {canAddOrder && <TeamUpsell team={team} canAddOrderWithOverrides={canAddOrderWithOverrides} />}
    </>
  )
}

export default TeamProductChange
